import {
  WSActions,
  WSControlGroup,
  WSForm,
  WSInputText,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  MfaFactorCreate,
  MfaFactorCreateType
} from "@wingspanhq/wingspan-api-sdk";
import { ErrorAlert } from "@components/ErrorAlert";
import React from "react";
import * as Yup from "yup";
import {
  FormDataPhoneNumber,
  FormPartialPhoneNumber,
  phoneFormDataToE164,
  validationSchemaPhoneNumber
} from "../../../modules/Onboarding/components/FormPartialPhoneNumber";
import { validatorEmail } from "../../../shared/validators/validatorEmail";
import { ENROLLABLE_FACTOR_TYPES, mfaFactorTypeName } from "./mfaFactorEnroll";

type FormData = {
  type: MfaFactorCreateType;
  email: string;
  phone: FormDataPhoneNumber;
};

export type MfaFactorEnrollSubmit = {
  create: MfaFactorCreate;
  destination: string;
};

type Props = {
  defaultEmail?: string;
  defaultPhone?: FormDataPhoneNumber;
  submitting: boolean;
  error?: unknown;
  onSubmit: (params: MfaFactorEnrollSubmit) => void;
};

const validationSchema = Yup.object().shape({
  type: Yup.string().required("Select a method"),
  email: Yup.string().when("type", {
    is: MfaFactorCreateType.EMAIL,
    then: validatorEmail.required("Email is required")
  }),
  phone: Yup.object().when("type", {
    is: MfaFactorCreateType.SMS,
    then: validationSchemaPhoneNumber
  })
});

export const MfaFactorEnrollForm: React.FC<Props> = props => {
  const handleSubmit = (data: FormData) => {
    if (data.type === MfaFactorCreateType.SMS) {
      const destination = phoneFormDataToE164(data.phone);
      props.onSubmit({
        create: {
          type: MfaFactorCreateType.SMS,
          label: destination,
          smsPhone: destination
        },
        destination
      });
      return;
    }

    const destination = data.email.trim().toLowerCase();
    props.onSubmit({
      create: {
        type: MfaFactorCreateType.EMAIL,
        label: destination,
        emailAddress: destination
      },
      destination
    });
  };

  return (
    <WSForm<FormData>
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      defaultValues={{
        type: MfaFactorCreateType.SMS,
        email: props.defaultEmail ?? "",
        phone: props.defaultPhone ?? { countryCode: "1", phoneNumber: "" }
      }}
    >
      <WSList gap="XL">
        <WSText color="gray600">
          Add a way to verify it's you when signing in or approving sensitive
          changes.
        </WSText>

        <WSForm.Field
          name="type"
          label="Method"
          component={WSControlGroup}
          componentProps={{
            type: "radio",
            options: ENROLLABLE_FACTOR_TYPES.map(type => ({
              value: type,
              label: mfaFactorTypeName(type)
            }))
          }}
        />

        <WSForm.Value name="type">
          {(type: MfaFactorCreateType) =>
            type === MfaFactorCreateType.SMS ? (
              <FormPartialPhoneNumber name="phone" label="Phone number" />
            ) : (
              <WSForm.Field
                name="email"
                label="Email address"
                component={WSInputText}
                componentProps={{ type: "email", autoComplete: "email" }}
              />
            )
          }
        </WSForm.Value>

        <ErrorAlert error={props.error} context="MfaFactor" />

        <WSActions
          buttons={[
            {
              label: "Send code",
              type: "submit",
              fullWidth: true,
              loading: props.submitting
            }
          ]}
        />
      </WSList>
    </WSForm>
  );
};
